import { observer } from 'mobx-react-lite';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StorePerformanceInterval, createStorePerformanceRequest } from '../../../../../constants';
import { useEventContext } from '../../../../../global-state/eventContext';
import { RfidCountResponse, useAsyncAction, useRfid } from '../../../../../hooks';
import { Icons, Typography } from '../../../../../stories/atoms';
import { ColumnFlexContainer, RowFlexContainer } from '../../../../../stories/atoms/containerV2/ContainerV2';
import { homeTranslations } from '../../../../../translations';
import { areaResultTranslations } from '../../../../../translations/areaResult';
import { isFailureResponse } from '../../../../../utils';
import { WidgetCardContent } from '../../../common/widgetCardContent/WidgetCardContent';
import { CircleSection, StyledIcon } from './RfidWidgets.styles';

export type RfidCountWidgetProps = {
  interval: StorePerformanceInterval;
  currentDate: Date;
};

export const RfidCountWidget: FC<RfidCountWidgetProps> = observer(({ interval, currentDate }) => {
  const { t } = useTranslation();
  const { rfidCount: rfidCountTranslations } = homeTranslations;
  const { customerExperience } = areaResultTranslations;

  const { fetchRfidCountData } = useRfid();
  const { transactionEventsCount } = useEventContext();

  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const {
    result: rfidCountData,
    isLoading: rfidCountDataLoading,
    isError: rfidCountDataError,
    triggerAction: triggerFetchRfidCountData,
  } = useAsyncAction<RfidCountResponse>();

  const fetchData = async () => {
    const request = createStorePerformanceRequest({
      interval,
      currentDate,
    });

    const response = await fetchRfidCountData(request);

    if (isFailureResponse(response)) {
      throw new Error();
    }

    setLastUpdated(response.data.lastUpdated);
    return response.data;
  };

  const fetchWidgetData = () => {
    triggerFetchRfidCountData(fetchData);
  };

  useEffect(() => {
    fetchWidgetData();
  }, [currentDate, transactionEventsCount]);

  const isNoData =
    !rfidCountData ||
    [rfidCountData.counts, rfidCountData.timeSpent, rfidCountData.scannersUsed].every(
      (value) => value === 0 || value === undefined,
    );

  return (
    <WidgetCardContent
      headerIcon={Icons.PROFILE}
      headerText={t(rfidCountTranslations.title.key, rfidCountTranslations.title.defaultValue)}
      subHeaderText={t(rfidCountTranslations.subTitle.key, rfidCountTranslations.subTitle.defaultValue)}
      isLoading={rfidCountDataLoading}
      isError={rfidCountDataError}
      errorText={t(rfidCountTranslations.error.key, rfidCountTranslations.error.defaultValue)}
      errorRetryHandler={fetchWidgetData}
      isNoData={isNoData}
      lastUpdatedDate={lastUpdated}
      toolTipText={t(rfidCountTranslations.toolTip.key, rfidCountTranslations.toolTip.defaultValue)}
      content={
        <ColumnFlexContainer gap={[2]} padding={[4]} flex={1} data-testid="rfid-count-wrapper">
          {rfidCountData?.counts && (
            <RowFlexContainer
              flex={1}
              padding={[2, 4]}
              backgroundColor="backgroundSelectedHover"
              borderRadius={[4]}
              alignItems="center"
              data-testid="counts-section"
            >
              <ColumnFlexContainer flex={1}>
                <RowFlexContainer alignItems="center" padding={[0, 0, 2]}>
                  <StyledIcon icon={Icons.HOURLASS} size="small" />
                  <Typography type="button" margin={[0, 2, 0]}>
                    {t(
                      rfidCountTranslations.countText.title.key,
                      rfidCountTranslations.countText.title.defaultValue,
                    )}
                  </Typography>
                </RowFlexContainer>

                <Typography>
                  {t(
                    rfidCountTranslations.countText.subTitle.key,
                    rfidCountTranslations.countText.subTitle.defaultValue,
                  )}
                </Typography>
              </ColumnFlexContainer>
              <CircleSection
                width={[15]}
                height={[15]}
                justifyContent="center"
                alignItems="center"
                borderRadius={[10]}
                margin={[0, 0, 0, 2]}
                shadow
              >
                <Typography type="button">{rfidCountData?.counts.toFixed()}</Typography>
              </CircleSection>
            </RowFlexContainer>
          )}
          {rfidCountData?.timeSpent && (
            <RowFlexContainer
              flex={1}
              padding={[2, 4]}
              backgroundColor="backgroundSelectedHover"
              borderRadius={[4]}
              alignItems="center"
              data-testid="time-spent-section"
            >
              <ColumnFlexContainer flex={1}>
                <RowFlexContainer alignItems="center" padding={[0, 0, 2]}>
                  <StyledIcon icon={Icons.CLOCK} size="small" />
                  <Typography type="button" margin={[0, 2, 0]}>
                    {t(customerExperience.hoursSpent.key, customerExperience.hoursSpent.defaultValue)}
                  </Typography>
                </RowFlexContainer>

                <Typography>
                  {t(
                    rfidCountTranslations.timeText.subTitle.key,
                    rfidCountTranslations.timeText.subTitle.defaultValue,
                  )}
                </Typography>
              </ColumnFlexContainer>
              <CircleSection
                width={[15]}
                height={[15]}
                justifyContent="center"
                alignItems="center"
                borderRadius={[10]}
                margin={[0, 0, 0, 2]}
                shadow
              >
                <Typography type="button">{rfidCountData?.timeSpent.toFixed(1)}</Typography>
              </CircleSection>
            </RowFlexContainer>
          )}
          {rfidCountData?.scannersUsed && (
            <RowFlexContainer
              flex={1}
              padding={[2, 4]}
              backgroundColor="backgroundSelectedHover"
              borderRadius={[4]}
              alignItems="center"
              data-testid="scanners-section"
            >
              <ColumnFlexContainer flex={1}>
                <RowFlexContainer alignItems="center" padding={[0, 0, 2]}>
                  <StyledIcon icon={Icons.SCAN} size="small" />
                  <Typography type="button" margin={[0, 2]}>
                    {t(
                      rfidCountTranslations.scannerText.title.key,
                      rfidCountTranslations.scannerText.title.defaultValue,
                    )}
                  </Typography>
                </RowFlexContainer>

                <Typography>
                  {t(
                    rfidCountTranslations.scannerText.subTitle.key,
                    rfidCountTranslations.scannerText.subTitle.defaultValue,
                  )}
                </Typography>
              </ColumnFlexContainer>

              <CircleSection
                width={[15]}
                height={[15]}
                justifyContent="center"
                alignItems="center"
                borderRadius={[10]}
                margin={[0, 0, 0, 2]}
                shadow
              >
                <Typography
                  type="button"
                  color={
                    rfidCountData?.scannersUsed && rfidCountData?.scannersUsed < 2 ? 'red' : 'validationGreen'
                  }
                >
                  {rfidCountData?.scannersUsed.toFixed()}
                </Typography>
              </CircleSection>
            </RowFlexContainer>
          )}
        </ColumnFlexContainer>
      }
    />
  );
});
